import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { navQueryOptions } from "@/lib/navigation.functions";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { installBreadcrumbs, addBreadcrumb } from "../lib/breadcrumbs";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ConsentBanner } from "@/components/site/ConsentBanner";
import { TrackingLoader } from "@/components/site/TrackingLoader";
import { PaymentTestModeBanner } from "@/components/site/PaymentTestModeBanner";
import { Toaster } from "@/components/ui/sonner";
import { ClientOnly } from "@tanstack/react-router";
import { AdminPublishBar } from "@/components/site/AdminPublishBar";

import { ThemeApplier } from "@/components/site/ThemeApplier";
import { supabase } from "@/integrations/supabase/client";
import { ORGANIZATION_JSON_LD } from "@/lib/business-info";

function NotFoundComponent() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  const isDev = import.meta.env.DEV;
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-lg text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. The issue has been reported.
        </p>
        <div className="mt-6 flex justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            Try again
          </button>
          <a
            href="/"
            className="rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium hover:bg-secondary"
          >
            Go home
          </a>
        </div>
        {isDev && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-xs text-muted-foreground">Error details (dev only)</summary>
            <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-muted p-3 text-left text-xs">
              {error?.message}
              {error?.stack ? `\n\n${error.stack}` : ""}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Admin-editable header/footer menus are prefetched so they server-render
  // from the database (no flicker, and crawlers see the real links).
  loader: ({ context }) => context.queryClient.ensureQueryData(navQueryOptions()),
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#0a1a0f" },
      { name: "author", content: "Glow Long Golf" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Glow Long Golf" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Glow Long Golf" },
      { property: "og:title", content: "Glow Long Golf" },
      { name: "twitter:title", content: "Glow Long Golf" },
      { name: "description", content: "Glow Long Golf Hub is a fun, energetic e-commerce site for glow-in-the-dark golf balls." },
      { property: "og:description", content: "Glow Long Golf Hub is a fun, energetic e-commerce site for glow-in-the-dark golf balls." },
      { name: "twitter:description", content: "Glow Long Golf Hub is a fun, energetic e-commerce site for glow-in-the-dark golf balls." },
      // og:image / twitter:image are set per-route. Do NOT set them here —
      // the root head() concatenates into every match and would override leaves.

    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@500;600;700;800&family=Inter:wght@400;500;600;700&display=swap",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(ORGANIZATION_JSON_LD),
      },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    installBreadcrumbs();
    const onError = (event: ErrorEvent) => {
      reportLovableError(event.error ?? event.message, {
        boundary: "window_onerror",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      reportLovableError(event.reason, { boundary: "window_unhandledrejection" });
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    const { data } = supabase.auth.onAuthStateChange((event) => {
      addBreadcrumb({ category: "auth", message: event });
      // NOTE: USER_UPDATED is intentionally excluded — supabase.auth.getUser()
      // (called by the _authenticated gate's beforeLoad) can emit USER_UPDATED
      // when it refreshes cached user metadata. Invalidating the router here
      // re-runs beforeLoad → calls getUser() → fires USER_UPDATED → infinite
      // loop, leaving /admin (and any other protected route) stuck blank.
      if (event === "SIGNED_IN" || event === "SIGNED_OUT") {
        router.invalidate();
        if (event === "SIGNED_IN") queryClient.invalidateQueries();
      }
    });
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      data.subscription.unsubscribe();
    };
  }, [router, queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeApplier />
      <PaymentTestModeBanner />
      <div className="flex min-h-dvh flex-col">
        <Header />
        <main className="flex-1">
          <Outlet />
        </main>
        <Footer />
      </div>
      <ConsentBanner />
      <TrackingLoader />
      <ClientOnly>
        <AdminPublishBar />
      </ClientOnly>
      <Toaster theme="dark" position="top-center" />

    </QueryClientProvider>
  );
}
